import { Dropdown, useConfirmDialog } from "@fluidattacks/design";
import type { IIconModifiable } from "@fluidattacks/design";
import { type FC, Fragment } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import type { IVulnerabilityPoliciesData } from "../../types";
import { useFindingPoliciesActions } from "../hooks";
import { OrganizationFindingPolicyStatus } from "gql/graphql";
import { useAuthz } from "hooks/use-authz";

type TIconName = IIconModifiable["icon"];
interface IPoliciesActionsProps {
  readonly cell: IVulnerabilityPoliciesData;
  readonly organizationId: string;
}

const tPath = "organization.tabs.policies.findings.";

const PoliciesActions: FC<IPoliciesActionsProps> = ({
  cell: { id, status, treatmentAcceptance },
  organizationId,
}): JSX.Element | null => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const { can } = useAuthz();
  const { organizationName } = useParams() as { organizationName: string };
  const {
    approvePolicy,
    deactivatePolicy,
    rejectPolicy,
    resubmitPolicy,
    loading,
    submitting,
  } = useFindingPoliciesActions(
    id,
    organizationId,
    organizationName,
    treatmentAcceptance,
  );

  // User role permissions
  const canDeactivateFindingPolicy = can(
    "integrates_api_mutations_deactivate_organization_finding_policy_mutate",
  );
  const canHandleFindingPolicy = can(
    "integrates_api_mutations_handle_organization_finding_policy_acceptance_mutate",
  );
  const canResubmitFindingPolicy = can(
    "integrates_api_mutations_submit_organization_finding_policy_mutate",
  );

  // Actions to perform by status
  const isSubmitted = status === OrganizationFindingPolicyStatus.Submitted;
  const isApproved = status === OrganizationFindingPolicyStatus.Approved;
  const isResubmitable =
    status === OrganizationFindingPolicyStatus.Inactive ||
    status === OrganizationFindingPolicyStatus.Rejected;
  const { color, icon, title } = isSubmitted
    ? {
        color: theme.palette.warning[500],
        icon: "exclamation-circle",
        title: "Pending approval",
      }
    : { color: undefined, icon: "ellipsis-vertical", title: "Manage" };

  const handleApprovePolicy = useCallback((): void => {
    void approvePolicy();
  }, [approvePolicy]);

  const handleRejectPolicy = useCallback((): void => {
    void rejectPolicy();
  }, [rejectPolicy]);

  const handleResubmitPolicy = useCallback(async (): Promise<void> => {
    const confirmResult = await confirm({
      title: t(`${tPath}submitPolicies.modalTitle`),
    });
    if (confirmResult) await resubmitPolicy();
  }, [confirm, resubmitPolicy, t]);

  const handleDeactivatePolicy = useCallback(async (): Promise<void> => {
    const confirmResult = await confirm({
      title: t(`${tPath}deactivatePolicies.modalTitle`),
    });
    if (confirmResult) await deactivatePolicy();
  }, [confirm, deactivatePolicy, t]);

  return canDeactivateFindingPolicy || canHandleFindingPolicy ? (
    <Fragment>
      <Dropdown
        icon={icon as TIconName}
        iconColor={color}
        id={id}
        options={[
          ...(isSubmitted
            ? [
                {
                  disabled: !canHandleFindingPolicy || loading,
                  icon: "check-circle" as TIconName,
                  iconColor: theme.palette.success[700],
                  label: "Approve",
                  onClick: handleApprovePolicy,
                },
                {
                  disabled: !canHandleFindingPolicy || loading,
                  icon: "xmark-circle" as TIconName,
                  iconColor: theme.palette.error[700],
                  label: "Reject",
                  onClick: handleRejectPolicy,
                },
              ]
            : []),
          ...(isApproved
            ? [
                {
                  disabled: !canDeactivateFindingPolicy || loading,
                  icon: "ban" as TIconName,
                  iconColor: theme.palette.gray[700],
                  label: "Deactivate",
                  onClick: handleDeactivatePolicy,
                },
                {
                  disabled:
                    !(isResubmitable && canResubmitFindingPolicy) || submitting,
                  icon: "circle-arrow-up" as TIconName,
                  label: "Request",
                  onClick: handleResubmitPolicy,
                },
              ]
            : []),
          ...(isResubmitable
            ? [
                {
                  disabled: !canResubmitFindingPolicy || loading,
                  icon: "circle-arrow-up" as TIconName,
                  iconColor: theme.palette.warning[700],
                  label: "Request",
                  onClick: handleResubmitPolicy,
                },
                {
                  disabled:
                    !(isApproved && canDeactivateFindingPolicy) || loading,
                  icon: "ban" as TIconName,
                  label: "Deactivate",
                  onClick: handleDeactivatePolicy,
                },
              ]
            : []),
        ]}
        showChevron={isSubmitted}
        title={title}
      />
      <ConfirmDialog />
    </Fragment>
  ) : null;
};

export { PoliciesActions as policiesActions };
